import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Fab,
  MenuItem,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  CardMembership as MembershipIcon,
  Redeem as ServicePackageIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTheme, alpha } from '@mui/material/styles';
import { BRAND, brandAlpha } from '../../theme/brandTokens';
import useBrandPalette from '../../theme/useBrandPalette';
import { useAppData } from '../../context/AppDataContext';
import { useAuth } from '../../context/AuthContext';
import { useStaffRealtime } from '../../context/StaffRealtimeContext';
import { useToast } from '../../context/ToastContext';
import ScrollableDialog from '../../components/common/ScrollableDialog';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import AppointmentCheckoutDialog from '../../components/appointments/AppointmentCheckoutDialog';
import AgendaCalendarToolbar, { ALL_TEAM_MEMBERS_VALUE } from '../../components/appointments/AgendaCalendarToolbar';
import BookAppointmentDialog from '../../components/appointments/BookAppointmentDialog';
import EditAppointmentDialog from '../../components/appointments/EditAppointmentDialog';
import AppointmentDetailsDrawer from '../../components/appointments/AppointmentDetailsDrawer';
import IssueInvoiceDialog from '../../components/moloni/IssueInvoiceDialog';
import MoloniService from '../../services/moloniService';
import { isPortugueseStore } from '../../utils/portugueseStore';
import SalovareSelectField from '../../components/common/SalovareSelectField';
import { buildWorkingHoursByTeamMemberId, formatMinutesToTime, parseSlotIntervalMinutes, snapMinutesToSlot } from '../../utils/appointmentBookingUtils';
import { AppointmentStatus, getAppointmentStatusKey, getPaymentStatusKey, resolveOnRequestActingTeamMemberId, getPendingOnRequestCandidateIds } from '../../utils/appointmentStatusUtils';
import { resolveStaffStatusMessageKey } from '../../utils/appointmentCancelCopy';
import { getAgendaVisibleBounds } from '../../utils/agendaCalendarBounds';
import { getColumnOpeningHours, getTeamMemberWorkingPeriods } from '../../utils/agendaColumnHours';
import { getAgendaHourRowHeight, getAgendaPixelsPerMinute } from '../../utils/agendaCalendarLayout';
import ManagementPageLayout, { ManagementPageCard } from '../../components/layout/ManagementPageLayout';
import ManagementPageSkeleton, { ManagementContentSkeleton } from '../../components/layout/ManagementPageSkeleton';
import { usePermissions } from '../../hooks/usePermissions';
import useStorePlan from '../../hooks/useStorePlan';
import { Permissions } from '../../constants/Permissions';
import StoreService from '../../services/storeService';
import TeamMemberService from '../../services/teamMemberService';
import {
  getManagementAgendaAppointments,
  getOwnManagementAgendaAppointments,
  updateAppointmentStatus,
  getAppointmentProductUsage,
  markAppointmentPaid,
  setAppointmentServicePrice,
  deleteAppointment,
  changeAppointmentToRegular,
  confirmOnRequestAppointment,
  declineOnRequestAppointment,
} from '../../services/appointmentService';
import { normalizeLanguage, DEFAULT_LANGUAGE, getLocalizedValue } from '../../constants/Languages';
import { DEFAULT_CURRENCY, formatCurrency } from '../../utils/currencyUtils';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { filterAgendaVisibleTeamMembers } from '../../utils/teamMemberBookingUtils';
import { getAppointmentCheckoutBreakdown, getPendingProductSaleLines } from '../../utils/appointmentCheckoutUtils';
import useSyncedHorizontalScroll from '../../hooks/useSyncedHorizontalScroll';
import { buildManagementAppointmentsPath } from '../../utils/staffNotificationNavigation';
import { isApiErrorCode } from '../../utils/apiErrorCode';
import { resolveAgendaStatusColors } from '../../utils/serviceValueFormat';
import { applyAppointmentServicePrice } from '../../utils/appointmentBookingUtils';

const borderColor = BRAND.line;
const headerBackground = BRAND.background;
const mutedText = BRAND.textMuted;
const hourColumnBackground = BRAND.background;
const closedSegmentBackground = brandAlpha('text', 0.05);
const statusColors = {
  pending: { background: '#fef3c7', border: '#d4a017', text: '#7c5a00' },
  cancelled: { background: '#f1f5f9', border: '#64748b', text: '#475569' },
  confirmed: { background: '#dbeafe', border: '#2563eb', text: '#1d4ed8' },
  completed: { background: '#e8f5e9', border: '#2e7d32', text: '#1b5e20' },
  noShow: { background: '#ffe4e6', border: '#e11d48', text: '#9f1239' },
};
const paymentStatusColors = {
  paid: { background: '#e8f5e9', border: '#2e7d32', text: '#1b5e20' },
  pendingPayment: { background: '#fff3e0', border: '#ef6c00', text: '#e65100' },
  refunded: { background: '#e0f2fe', border: '#0369a1', text: '#075985' },
  partiallyRefunded: { background: '#e0f2fe', border: '#0369a1', text: '#075985' },
  disputed: { background: '#ffebee', border: '#c62828', text: '#b71c1c' },
  unpaid: { background: '#f3f4f6', border: '#9ca3af', text: '#4b5563' },
};
/** Brand-accent colour set (awaiting payment / deposit paid); concrete hex because it feeds alpha(). */
const getBrandColorSet = (brand) => ({
  background: alpha(brand.accent, 0.12),
  border: brand.accent,
  text: brand.accentText,
});
const timeOffColors = { background: '#f3f4f6', border: '#9ca3af', text: '#4b5563' };
const HOUR_LABEL_INTERVAL_MINUTES = 60;
const AGENDA_TOP_PADDING = 20;
const AGENDA_BOTTOM_PADDING = 20;
const APPOINTMENT_HORIZONTAL_INSET = 10;
const STACK_HORIZONTAL_PEEK = 12;
const MIN_APPOINTMENT_HEIGHT = 34;
const COMPACT_APPOINTMENT_MAX_HEIGHT = 56;
const APPOINTMENT_HOVER_EXPANDED_HEIGHT = 88;
const APPOINTMENT_HOVER_Z_INDEX = 3;
const DAY_COLUMN_MIN_WIDTH = 168;
const TEAM_MEMBER_COLUMN_MIN_WIDTH = 188;
const TIME_COLUMN_WIDTH = 48;
const AGENDA_STICKY_HEADER_Z_INDEX = 3;
const AGENDA_APPOINTMENT_BASE_Z_INDEX = 1;
const CURRENT_TIME_LINE_Z_INDEX = 8;
const CURRENT_TIME_LABEL_SIDE_MARGIN = 3;
const AGENDA_PAGE_CHROME_HEIGHT = 64;
const hiddenScrollbarSx = {
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
};
const agendaPageLayoutSx = {
  height: `calc(100dvh - ${AGENDA_PAGE_CHROME_HEIGHT}px)`,
  maxHeight: `calc(100dvh - ${AGENDA_PAGE_CHROME_HEIGHT}px)`,
  minHeight: 0,
  overflow: 'hidden',
  py: 0,
  px: 0,
};
const agendaPageCardSx = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  borderRadius: 0,
  boxShadow: 'none',
  background: BRAND.surface,
};
const agendaPageContentSx = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
};

const backendLanguageKeyByCode = {
  en: 'EN',
  pt: 'PT',
  es: 'ES',
};

const backendEnumLanguageKey = {
  1: 'PT',
  2: 'EN',
  3: 'ES',
};

function startOfDay(date) {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function addDays(date, amount) {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + amount);
  return startOfDay(copy);
}

function formatDateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function parseQueryDate(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : startOfDay(date);
}

function parseTimeToMinutes(value) {
  if (!value || typeof value !== 'string') return 0;
  const [hours, minutes] = value.split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
}

function formatMinutes(minutes) {
  const safe = Math.max(0, minutes);
  const hours = Math.floor(safe / 60);
  const mins = safe % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

function resolveRequestedBookStartTime(requestedTime, slotInterval) {
  if (!/^\d{2}:\d{2}$/.test(requestedTime || '')) {
    return '';
  }

  const interval = parseSlotIntervalMinutes(slotInterval);
  const snapped = snapMinutesToSlot(parseTimeToMinutes(requestedTime), interval);
  const latest = Math.max(interval, (24 * 60) - interval);
  return formatMinutesToTime(Math.min(Math.max(snapped, 0), latest));
}

function getDisplayPeriod(services, fallbackPeriod) {
  const validPeriods = (services || []).map((service) => service?.period).filter((period) => period?.from && period?.to);
  if (validPeriods.length === 0) {
    return fallbackPeriod;
  }

  const orderedPeriods = [...validPeriods].sort((left, right) => parseTimeToMinutes(left.from) - parseTimeToMinutes(right.from));
  return {
    from: orderedPeriods[0].from,
    to: [...orderedPeriods].sort((left, right) => parseTimeToMinutes(right.to) - parseTimeToMinutes(left.to))[0].to,
  };
}

function getTimeOffDisplayPeriod(timeOff, visibleBounds, openingPeriods) {
  if (timeOff?.period?.from && timeOff?.period?.to) {
    return timeOff.period;
  }

  if (openingPeriods.length > 0) {
    const from = Math.min(...openingPeriods.map((period) => parseTimeToMinutes(period.from)));
    const to = Math.max(...openingPeriods.map((period) => parseTimeToMinutes(period.to)));
    return { from: formatMinutes(from), to: formatMinutes(to) };
  }

  return {
    from: formatMinutes(visibleBounds.earliest),
    to: formatMinutes(visibleBounds.latest),
  };
}

function isFullDayTimeOff(timeOff) {
  return !timeOff?.period?.from || !timeOff?.period?.to;
}

const NOT_COLLECTABLE_PAYMENT_KEYS = new Set(['paid', 'refunded', 'partiallyRefunded', 'disputed']);

function canMarkAppointmentPaid(appointment) {
  return !NOT_COLLECTABLE_PAYMENT_KEYS.has(getPaymentStatusKey(appointment?.paymentStatus))
    && !appointment?.pendingMembershipEnrollment
    && getAppointmentCheckoutBreakdown(appointment).collectableDue > 0;
}

const EMPTY_BOOK_SERVICE_IDS = [];

function getBookNextPrefill(appointment) {
  const serviceIds = [...new Set(
    (appointment?.services || [])
      .map((service) => Number(service.serviceId))
      .filter((id) => Number.isFinite(id) && id > 0),
  )];

  const customerId = appointment?.customerId;
  const hasCustomer = Boolean(customerId) && String(customerId) !== '00000000-0000-0000-0000-000000000000';

  return {
    customer: hasCustomer
      ? { customerId, name: appointment.customerName }
      : null,
    serviceIds,
    customerMembershipId: appointment?.customerMembershipId || null,
    membershipPlanId: appointment?.pendingMembershipPlanId || appointment?.membershipPlanId || null,
  };
}

function applyPaidCheckoutFields(item, checkout) {
  if (!checkout) {
    return {
      ...item,
      amountDue: 0,
      paymentStatus: 2,
    };
  }

  const payments = checkout.payments?.length
    ? checkout.payments
    : (checkout.paymentMethod
      ? [{ paymentMethod: checkout.paymentMethod, amount: checkout.amountReceived ?? 0 }]
      : item.payments);

  return {
    ...item,
    amountDue: 0,
    paymentStatus: 2,
    amountReceived: checkout.amountReceived ?? item.amountReceived ?? 0,
    storePaymentMethod: checkout.paymentMethod ?? item.storePaymentMethod,
    payments,
  };
}

function isAssignedToAppointment(appointment, ownTeamMemberId) {
  if (!appointment || !ownTeamMemberId) return false;

  const ownId = String(ownTeamMemberId).toLowerCase();
  const services = appointment.services || [];
  if (services.some((service) => String(service.teamMemberId).toLowerCase() === ownId)) {
    return true;
  }

  return getPendingOnRequestCandidateIds(appointment).some((id) => String(id).toLowerCase() === ownId);
}

function canEditAppointment(appointment, canUpdateAll, canUpdateOwn, ownTeamMemberId) {
  if (!appointment) return false;

  const statusKey = getAppointmentStatusKey(appointment.status);
  if (statusKey !== 'confirmed' && statusKey !== 'pending') return false;

  if (canUpdateAll) return true;

  return canUpdateOwn && isAssignedToAppointment(appointment, ownTeamMemberId);
}

function canDeleteAppointment(appointment, canDelete) {
  if (!canDelete || !appointment) return false;

  const statusKey = getAppointmentStatusKey(appointment.status);
  return statusKey !== 'completed';
}

function canUpdateAppointmentStatus(appointment, canReadAllAgendas, ownTeamMemberId) {
  if (!appointment) return false;

  const statusKey = getAppointmentStatusKey(appointment.status);
  if (statusKey !== 'confirmed' && statusKey !== 'pending') return false;

  if (canReadAllAgendas) return true;

  return isAssignedToAppointment(appointment, ownTeamMemberId);
}

function resolveServiceName(nameDictionary, languageCode, t) {
  if (!nameDictionary || typeof nameDictionary !== 'object') {
    return t('managementAppointments.unnamedService');
  }

  const preferredKey = backendLanguageKeyByCode[languageCode] || 'EN';
  if (nameDictionary[preferredKey]) return nameDictionary[preferredKey];
  if (nameDictionary[preferredKey.toLowerCase()]) return nameDictionary[preferredKey.toLowerCase()];

  const enumEntry = Object.entries(nameDictionary).find(([key]) => backendEnumLanguageKey[Number(key)] === preferredKey);
  if (enumEntry?.[1]) return enumEntry[1];

  const firstValue = Object.values(nameDictionary).find((value) => typeof value === 'string' && value.trim().length > 0);
  return firstValue || t('managementAppointments.unnamedService');
}

function getWeekdayLabel(date, locale) {
  return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date);
}

function getWeekdayInitial(date, locale) {
  const weekdayLabel = getWeekdayLabel(date, locale);
  return weekdayLabel.charAt(0).toUpperCase();
}

function getDateLabel(date, locale) {
  return new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short' }).format(date);
}

function getDayOnly(date) {
  return date.getDate().toString();
}

function getInitials(name) {
  if (!name || typeof name !== 'string') return '?';

  const parts = name
    .split(' ')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function isSameCalendarDay(left, right) {
  return left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth() && left.getDate() === right.getDate();
}

function getTeamMemberPicture(teamMember) {
  return teamMember?.picture || teamMember?.Picture || null;
}

function getRangeLabel(days, locale, compact = false) {
  if (days.length === 0) return '';
  const first = days[0];
  const last = days[days.length - 1];
  const sameDay = formatDateKey(first) === formatDateKey(last);

  if (sameDay) {
    const formatOptions = compact
      ? { weekday: 'short', day: '2-digit', month: 'short' }
      : { weekday: 'long', day: '2-digit', month: 'long' };
    return new Intl.DateTimeFormat(locale, formatOptions).format(first);
  }

  return `${new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short' }).format(first)} - ${new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short' }).format(last)}`;
}

function getClosedSegments(periods, visibleBounds, openingHoursEntry) {
  if (openingHoursEntry == null) {
    return [];
  }

  if (!periods || periods.length === 0) {
    return [{ from: visibleBounds.earliest, to: visibleBounds.latest }];
  }

  const orderedPeriods = [...periods]
    .map((period) => ({ from: parseTimeToMinutes(period.from), to: parseTimeToMinutes(period.to) }))
    .sort((left, right) => left.from - right.from);

  const segments = [];
  let cursor = visibleBounds.earliest;

  orderedPeriods.forEach((period) => {
    if (period.from > cursor) {
      segments.push({ from: cursor, to: period.from });
    }
    cursor = Math.max(cursor, period.to);
  });

  if (cursor < visibleBounds.latest) {
    segments.push({ from: cursor, to: visibleBounds.latest });
  }

  return segments.filter((segment) => segment.to > segment.from);
}

function getAppointmentTimeRange(appointment) {
  const period = appointment.displayPeriod || appointment.period;
  const from = parseTimeToMinutes(period?.from);
  const to = parseTimeToMinutes(period?.to);

  return {
    from,
    to: Math.max(to, from + 1),
  };
}

function layoutOverlappingAppointments(appointments) {
  if (!appointments?.length) {
    return [];
  }

  const items = appointments.map((appointment) => {
    const { from, to } = getAppointmentTimeRange(appointment);
    return { appointment, from, to };
  }).sort((left, right) => left.from - right.from || left.to - right.to);

  const clusters = [];
  let currentCluster = [];
  let clusterEnd = -1;

  items.forEach((item) => {
    if (currentCluster.length === 0 || item.from < clusterEnd) {
      currentCluster.push(item);
      clusterEnd = Math.max(clusterEnd, item.to);
      return;
    }

    clusters.push(currentCluster);
    currentCluster = [item];
    clusterEnd = item.to;
  });

  if (currentCluster.length > 0) {
    clusters.push(currentCluster);
  }

  const layouts = [];

  clusters.forEach((cluster) => {
    const stackCount = cluster.length;
    cluster.forEach((item, stackIndex) => {
      layouts.push({
        appointment: item.appointment,
        stackIndex,
        stackCount,
      });
    });
  });

  return layouts;
}

function getAppointmentBlockMetrics(from, to, pixelsPerMinute) {
  const durationMinutes = Math.max(1, to - from);
  const naturalHeight = durationMinutes * pixelsPerMinute - 2;
  const isCompact = naturalHeight < COMPACT_APPOINTMENT_MAX_HEIGHT;
  const fullHeight = isCompact ? Math.max(MIN_APPOINTMENT_HEIGHT, naturalHeight) : naturalHeight;

  return { durationMinutes, isCompact, fullHeight };
}

function AgendaAppointmentCard({
  appointment,
  stackIndex,
  stackCount,
  visibleBounds,
  pixelsPerMinute,
  languageCode,
  t,
  onSelect,
  invoiceLabel,
}) {
  const renderPeriod = appointment.displayPeriod || appointment.period;
  const from = parseTimeToMinutes(renderPeriod?.from);
  const to = parseTimeToMinutes(renderPeriod?.to);
  const isStacked = stackCount > 1;
  const fullTop = (from - visibleBounds.earliest) * pixelsPerMinute + 1;
  const { isCompact, fullHeight } = getAppointmentBlockMetrics(from, to, pixelsPerMinute);
  const timeLabel = `${renderPeriod?.from?.slice(0, 5)} - ${renderPeriod?.to?.slice(0, 5)}`;
  const stackedWidth = `calc(100% - ${APPOINTMENT_HORIZONTAL_INSET * 2}px - ${stackIndex * STACK_HORIZONTAL_PEEK}px)`;
  const brand = useBrandPalette();
  const statusKey = getAppointmentStatusKey(appointment.status);
  const paymentKey = getPaymentStatusKey(appointment.paymentStatus);
  const colorSet = statusKey === 'awaitingPayment'
    ? getBrandColorSet(brand)
    : resolveAgendaStatusColors(
      statusKey,
      appointment.hasUnpricedServices,
      statusColors[statusKey] || { background: '#f3f4f6', border: '#9ca3af', text: '#374151' },
    );
  const paymentColorSet = paymentKey === 'depositPaid'
    ? getBrandColorSet(brand)
    : (paymentStatusColors[paymentKey] || paymentStatusColors.unpaid);
  const showPaymentChip = !['unpaid', 'pendingPayment'].includes(paymentKey)
    || getAppointmentCheckoutBreakdown(appointment).collectableDue > 0;
  const displayServices = appointment.displayServices || appointment.services || [];
  const serviceNames = displayServices.map((service) => resolveServiceName(service.name, languageCode, t)).join(', ');

  return (
    <Box
      onClick={(event) => {
        event.stopPropagation();
        onSelect(appointment);
      }}
      sx={{
        position: 'absolute',
        top: AGENDA_TOP_PADDING + fullTop,
        left: APPOINTMENT_HORIZONTAL_INSET,
        right: isStacked ? 'auto' : APPOINTMENT_HORIZONTAL_INSET,
        width: isStacked ? stackedWidth : 'auto',
        height: fullHeight,
        borderRadius: 2,
        border: `1px solid ${alpha(colorSet.border, 0.35)}`,
        borderLeft: `4px solid ${colorSet.border}`,
        bgcolor: colorSet.background,
        color: colorSet.text,
        px: isCompact ? 0.75 : 1.25,
        py: isCompact ? 0.35 : 0.75,
        overflow: 'hidden',
        cursor: 'pointer',
        boxShadow: isStacked ? '0 1px 3px rgba(15,23,42,0.12)' : '0 1px 2px rgba(15,23,42,0.08)',
        zIndex: AGENDA_APPOINTMENT_BASE_Z_INDEX + stackIndex,
        transition: 'width 0.15s ease, box-shadow 0.15s ease, min-height 0.15s ease, z-index 0s',
        '& .appointment-card-expanded': {
          display: 'none',
        },
        '&:hover': {
          right: APPOINTMENT_HORIZONTAL_INSET,
          width: 'auto',
          minHeight: isCompact ? APPOINTMENT_HOVER_EXPANDED_HEIGHT : fullHeight,
          zIndex: APPOINTMENT_HOVER_Z_INDEX,
          overflow: 'hidden',
          boxShadow: '0 8px 24px rgba(15,23,42,0.18)',
        },
        '&:hover .appointment-card-expanded': {
          display: 'block',
        },
      }}
    >
      {isCompact ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, overflow: 'hidden' }}>
            {(appointment.pendingMembershipEnrollment || appointment.coveredByMembership) && (
              <MembershipIcon sx={{ fontSize: 12, color: BRAND.accentText, flexShrink: 0 }} />
            )}
            {appointment.coveredByServicePackage && (
              <ServicePackageIcon sx={{ fontSize: 12, color: BRAND.accentText, flexShrink: 0 }} />
            )}
            {appointment.membershipRenewalRisk && (
              <WarningAmberIcon sx={{ fontSize: 12, color: '#e65100', flexShrink: 0 }} />
            )}
            <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
              {appointment.customerName}
            </Typography>
            {invoiceLabel ? (
              <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.1, flexShrink: 0 }}>{invoiceLabel}</Typography>
            ) : null}
          </Box>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, lineHeight: 1.1, opacity: 0.9 }}>
            {timeLabel}
          </Typography>
          <Box className="appointment-card-expanded">
            {showPaymentChip && (
            <Chip
              label={t(`managementAppointments.paymentStatus.${paymentKey}`)}
              size="small"
              sx={{
                height: 18,
                mt: 0.35,
                mb: 0.25,
                fontSize: '0.65rem',
                fontWeight: 700,
                bgcolor: paymentColorSet.background,
                color: paymentColorSet.text,
                border: `1px solid ${paymentColorSet.border}`,
              }}
            />
            )}
            <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, lineHeight: 1.25 }}>
              {serviceNames}
            </Typography>
          </Box>
        </>
      ) : (
        <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25, overflow: 'hidden' }}>
        {(appointment.pendingMembershipEnrollment || appointment.coveredByMembership) && (
          <MembershipIcon sx={{ fontSize: 14, color: BRAND.accentText, flexShrink: 0 }} />
        )}
        {appointment.coveredByServicePackage && (
          <ServicePackageIcon sx={{ fontSize: 14, color: BRAND.accentText, flexShrink: 0 }} />
        )}
        {appointment.membershipRenewalRisk && (
          <WarningAmberIcon sx={{ fontSize: 14, color: '#e65100', flexShrink: 0 }} />
        )}
        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {appointment.customerName}
        </Typography>
      </Box>
      {showPaymentChip && (
      <Chip
        label={t(`managementAppointments.paymentStatus.${paymentKey}`)}
        size="small"
        sx={{
          height: 18,
          mb: 0.25,
          fontSize: '0.65rem',
          fontWeight: 700,
          bgcolor: paymentColorSet.background,
          color: paymentColorSet.text,
          border: `1px solid ${paymentColorSet.border}`,
        }}
      />
      )}
      <Typography variant="caption" sx={{ display: '-webkit-box', opacity: 0.9, overflow: 'hidden', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.25 }}>
        {serviceNames}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, lineHeight: 1.1, mt: 0.35, opacity: 0.9 }}>
        {timeLabel}
      </Typography>
        </>
      )}
    </Box>
  );
}

export default function ManagementAppointmentsPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const isCompactViewport = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const hourRowHeight = getAgendaHourRowHeight({ isPhone, isCompactViewport });
  const pixelsPerMinute = getAgendaPixelsPerMinute(hourRowHeight);
  const languageCode = normalizeLanguage(i18n.language) || DEFAULT_LANGUAGE;
  const locale = languageCode === 'pt' ? 'pt-PT' : languageCode === 'es' ? 'es-ES' : 'en-US';
  const { currentStore } = useAppData();
  const { managementUser } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasAnyPermission, hasPermission, loading: permissionsLoading } = usePermissions(currentStore?.id);
  const { isPro, entitlements } = useStorePlan(currentStore?.id, Boolean(currentStore?.id));
  const hideProducts = !isPro || entitlements?.allowsProducts === false;
  const { subscribeToCalendarChanges } = useStaffRealtime();
  const { showSuccess } = useToast();

  const [viewDays, setViewDays] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia('(max-width: 899.95px)').matches ? 1 : 3
  ));
  const [anchorDate, setAnchorDate] = useState(() => startOfDay(new Date()));
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const [storeDetails, setStoreDetails] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const hideProfessionalPicker = teamMembers.length <= 1;
  const [selectedTeamMemberIds, setSelectedTeamMemberIds] = useState([]);
  const [committedTeamMemberIds, setCommittedTeamMemberIds] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [timeOffs, setTimeOffs] = useState([]);
  const [specialOpenings, setSpecialOpenings] = useState([]);
  const [agendaWorkingHours, setAgendaWorkingHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailsAppointment, setDetailsAppointment] = useState(null);
  const [detailsTimeOff, setDetailsTimeOff] = useState(null);
  const [completingAppointmentId, setCompletingAppointmentId] = useState(null);
  const [markingPaidAppointmentId, setMarkingPaidAppointmentId] = useState(null);
  const [invoicePromptId, setInvoicePromptId] = useState(null);
  const [invoiceFlags, setInvoiceFlags] = useState({});
  const [moloniReady, setMoloniReady] = useState(false);
  const moloniReadyPromise = useRef(Promise.resolve(false));

  const portugueseStore = isPortugueseStore(currentStore?.country) || isPortugueseStore(storeDetails?.address?.country);

  useEffect(() => {
    if (!portugueseStore || !currentStore?.id) {
      moloniReadyPromise.current = Promise.resolve(false);
      setMoloniReady(false);
      return undefined;
    }

    let cancelled = false;
    const promise = MoloniService.getConnection(currentStore.id)
      .then((connection) => Boolean(connection?.ready ?? connection?.Ready))
      .catch(() => false);
    moloniReadyPromise.current = promise;
    promise.then((ready) => {
      if (!cancelled) setMoloniReady(ready);
    });
    return () => {
      cancelled = true;
    };
  }, [portugueseStore, currentStore?.id]);

  useEffect(() => {
    if (!moloniReady || !currentStore?.id || appointments.length === 0) {
      setInvoiceFlags({});
      return undefined;
    }

    let cancelled = false;
    const keys = appointments.map((item) => String(item.id).replace(/-/g, ''));
    MoloniService.flags(currentStore.id, keys)
      .then((list) => {
        if (cancelled) return;
        const next = {};
        (list || []).forEach((flag) => {
          if (flag.issued || flag.Issued) next[flag.sourceKey || flag.SourceKey] = true;
        });
        setInvoiceFlags(next);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [appointments, currentStore?.id, moloniReady]);
  const [changingToRegularAppointmentId, setChangingToRegularAppointmentId] = useState(null);
  const [bookSlot, setBookSlot] = useState(null);
  const [bookPrefill, setBookPrefill] = useState(null);
  const [agendaRefreshNonce, setAgendaRefreshNonce] = useState(0);
  const [editAppointment, setEditAppointment] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [checkoutAction, setCheckoutAction] = useState(null);
  const [deletingAppointmentId, setDeletingAppointmentId] = useState(null);
  const [respondingOnRequestId, setRespondingOnRequestId] = useState(null);
  const [onRequestActingTeamMemberId, setOnRequestActingTeamMemberId] = useState('');
  const openedQueryAppointmentRef = useRef(null);
  const openedBookCustomerRef = useRef(null);
  const storeDetailsStoreIdRef = useRef(null);
  const [storeDetailsRequestId, setStoreDetailsRequestId] = useState(0);

  const openAppointmentDetails = useCallback((appointment) => {
    setDetailsAppointment(appointment);
    if (!appointment?.id) {
      return;
    }

    openedQueryAppointmentRef.current = String(appointment.id).toLowerCase();
    const nextPath = buildManagementAppointmentsPath({
      appointmentId: appointment.id,
      date: appointment.date || searchParams.get('date'),
      from: searchParams.get('from'),
    });
    if (`${window.location.pathname}${window.location.search}` !== nextPath) {
      navigate(nextPath, { replace: true });
    }
  }, [navigate, searchParams]);

  const closeAppointmentDetails = useCallback(() => {
    setDetailsAppointment(null);
    openedQueryAppointmentRef.current = null;
    const nextPath = buildManagementAppointmentsPath({
      date: searchParams.get('date'),
      from: searchParams.get('from'),
    });
    if (`${window.location.pathname}${window.location.search}` !== nextPath) {
      navigate(nextPath, { replace: true });
    }
  }, [navigate, searchParams]);
  const {
    headerScrollRef,
    mainScrollRef,
    timeScrollRef,
    contentRef,
    verticalScrollbarWidth,
    handleHeaderScroll,
    handleMainScroll,
    handleTimeScroll,
  } = useSyncedHorizontalScroll(loading ? 'loading' : 'ready');

  useEffect(() => {
    if (!currentStore?.id) {
      return undefined;
    }

    return subscribeToCalendarChanges((change) => {
      const changeStoreId = change?.storeId ? String(change.storeId).toLowerCase() : '';
      const activeStoreId = String(currentStore.id).toLowerCase();
      if (!changeStoreId || changeStoreId === activeStoreId) {
        setAgendaRefreshNonce((value) => value + 1);
      }
    });
  }, [currentStore?.id, subscribeToCalendarChanges]);

  const activeStoreIdRef = useRef(currentStore?.id);
  const committedForStoreIdRef = useRef(null);
  const teamMembersStoreIdRef = useRef(null);

  if (activeStoreIdRef.current !== currentStore?.id) {
    activeStoreIdRef.current = currentStore?.id;
    committedForStoreIdRef.current = null;
    teamMembersStoreIdRef.current = null;
  }

  const commitTeamMemberSelection = useCallback((teamMemberIds) => {
    if (!currentStore?.id || teamMemberIds.length === 0) {
      committedForStoreIdRef.current = null;
      setCommittedTeamMemberIds([]);
      return;
    }

    committedForStoreIdRef.current = currentStore.id;
    setCommittedTeamMemberIds(teamMemberIds);
  }, [currentStore?.id]);

  const canReadAgenda = hasAnyPermission([Permissions.TeamMembersAgendaRead, Permissions.TeamMembersAgendaReadOwn]);
  const canReadAllAgendas = hasAnyPermission([Permissions.TeamMembersAgendaRead]);
  const canCreateAppointments = hasAnyPermission([Permissions.AppointmentsCreate]);
  const canUpdateAllAppointments = hasAnyPermission([Permissions.AppointmentsUpdate]);
  const canUpdateOwnAppointments = hasAnyPermission([Permissions.AppointmentsUpdateOwn]);
  const canDeleteAppointments = hasAnyPermission([Permissions.AppointmentsDelete]);
  const canMarkPaid = hasAnyPermission([Permissions.AppointmentsPaymentUpdate]);
  const canDiscountAppointments = hasPermission(Permissions.AppointmentsDiscount);
  const canReadCustomers = hasAnyPermission([Permissions.CustomersRead]);
  const ownTeamMemberId = managementUser?.id || managementUser?.userId || '';
  const ownTeamMemberName = managementUser?.name || managementUser?.unique_name || managementUser?.email || t('managementAppointments.myAgenda');
  const ownTeamMemberPicture = managementUser?.picture || managementUser?.Picture || null;

  const selectedTeamMembers = useMemo(() => {
    const selectedIdSet = new Set(selectedTeamMemberIds);
    return teamMembers.filter((teamMember) => selectedIdSet.has(teamMember.id));
  }, [teamMembers, selectedTeamMemberIds]);

  const isMultiTeamView = canReadAllAgendas && selectedTeamMemberIds.length > 1;
  const effectiveViewDays = isMultiTeamView ? 1 : viewDays;
  const allTeamMembersSelected = canReadAllAgendas && teamMembers.length > 0 && selectedTeamMemberIds.length === teamMembers.length;
  const selectedTeamMember = selectedTeamMembers[0] || null;

  const visibleDays = useMemo(() => Array.from({ length: effectiveViewDays }, (_, index) => addDays(anchorDate, index)), [anchorDate, effectiveViewDays]);
  const dateRange = useMemo(() => ({ from: formatDateKey(visibleDays[0]), to: formatDateKey(visibleDays[visibleDays.length - 1]) }), [visibleDays]);
  const workingHoursByTeamMemberId = useMemo(() => ({
    ...buildWorkingHoursByTeamMemberId(teamMembers),
    ...buildWorkingHoursByTeamMemberId(agendaWorkingHours),
  }), [teamMembers, agendaWorkingHours]);
  const visibleBounds = useMemo(() => {
    const visibleDateKeys = new Set(visibleDays.map((day) => formatDateKey(day)));
    return getAgendaVisibleBounds(visibleDays, storeDetails, [{
      appointments: appointments.filter((appointment) => visibleDateKeys.has(appointment.date)),
      timeOffs: timeOffs.filter((timeOff) => visibleDateKeys.has(timeOff.date)),
    }], getTeamMemberWorkingPeriods(workingHoursByTeamMemberId, selectedTeamMemberIds, visibleDays));
  }, [visibleDays, storeDetails, appointments, timeOffs, workingHoursByTeamMemberId, selectedTeamMemberIds]);
  const totalMinutes = Math.max(HOUR_LABEL_INTERVAL_MINUTES, visibleBounds.latest - visibleBounds.earliest);
  const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();
  const showCurrentTimeLine = currentMinutes >= visibleBounds.earliest && currentMinutes <= visibleBounds.latest;
  const hourMarks = useMemo(() => {
    const slots = [];
    for (let minute = visibleBounds.earliest; minute <= visibleBounds.latest; minute += HOUR_LABEL_INTERVAL_MINUTES) {
      slots.push(minute);
    }
    return slots;
  }, [visibleBounds]);

  useEffect(() => {
    const dateFromQuery = parseQueryDate(searchParams.get('date'));
    if (dateFromQuery) {
      setAnchorDate(dateFromQuery);
    }
  }, [searchParams]);

  useEffect(() => {
    const customerIdParam = searchParams.get('bookCustomer');
    if (!customerIdParam || permissionsLoading || !currentStore?.id) {
      return;
    }

    if (storeDetailsStoreIdRef.current !== currentStore.id) {
      return;
    }

    const clearBookingQuery = () => {
      if (!searchParams.has('bookCustomer') && !searchParams.has('startTime')) {
        return;
      }

      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('bookCustomer');
      nextParams.delete('startTime');
      const query = nextParams.toString();
      navigate(query ? `/management/appointments?${query}` : '/management/appointments', { replace: true });
    };

    if (!canCreateAppointments) {
      clearBookingQuery();
      return;
    }

    const customerKey = String(customerIdParam).toLowerCase();
    if (openedBookCustomerRef.current === customerKey) {
      clearBookingQuery();
      return;
    }

    openedBookCustomerRef.current = customerKey;
    const stateCustomer = location.state?.bookCustomer;
    const stateMatches = stateCustomer
      && String(stateCustomer.customerId || '').toLowerCase() === customerKey;

    setBookPrefill({
      customer: stateMatches
        ? stateCustomer
        : { customerId: customerIdParam, name: '' },
      serviceIds: [],
      customerMembershipId: null,
      membershipPlanId: null,
    });
    setBookSlot({
      date: searchParams.get('date') || formatDateKey(new Date()),
      startTime: resolveRequestedBookStartTime(searchParams.get('startTime'), storeDetails?.slotInterval),
    });
    clearBookingQuery();
  }, [
    canCreateAppointments,
    currentStore?.id,
    location.state,
    navigate,
    permissionsLoading,
    searchParams,
    storeDetails,
    storeDetailsRequestId,
  ]);

  useEffect(() => {
    const appointmentId = searchParams.get('appointment');
    if (!appointmentId || loading) {
      return;
    }

    const key = String(appointmentId).toLowerCase();
    if (openedQueryAppointmentRef.current === key) {
      return;
    }

    const match = appointments.find((item) => String(item.id).toLowerCase() === key);
    if (match) {
      openedQueryAppointmentRef.current = key;
      setDetailsAppointment(match);
    }
  }, [searchParams, appointments, loading]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, []);

  const scrolledToNowKeyRef = useRef(null);

  useLayoutEffect(() => {
    if (loading) {
      return undefined;
    }

    const includesToday = visibleDays.some((day) => isSameCalendarDay(day, currentTime));
    if (!includesToday || !showCurrentTimeLine) {
      return undefined;
    }

    const scrollKey = `${dateRange.from}:${dateRange.to}:${[...committedTeamMemberIds].sort().join(',')}:${hourRowHeight}`;
    if (scrolledToNowKeyRef.current === scrollKey) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      const main = mainScrollRef.current;
      if (!main || main.clientHeight <= 0) {
        return;
      }

      const nowOffset = AGENDA_TOP_PADDING + (currentMinutes - visibleBounds.earliest) * pixelsPerMinute;
      main.scrollTop = Math.max(0, nowOffset - Math.max(48, main.clientHeight / 3));
      scrolledToNowKeyRef.current = scrollKey;
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [
    loading,
    visibleDays,
    currentTime,
    showCurrentTimeLine,
    dateRange.from,
    dateRange.to,
    committedTeamMemberIds,
    hourRowHeight,
    currentMinutes,
    visibleBounds.earliest,
    pixelsPerMinute,
    mainScrollRef,
  ]);

  useEffect(() => {
    if (isMultiTeamView && viewDays !== 1) {
      setViewDays(1);
    }
  }, [isMultiTeamView, viewDays]);

  useEffect(() => {
    if (!currentStore?.id) {
      storeDetailsStoreIdRef.current = null;
      setStoreDetails(null);
      return undefined;
    }

    let cancelled = false;
    const storeIdAtStart = currentStore.id;
    storeDetailsStoreIdRef.current = null;
    const requestKey = `store-details-${storeIdAtStart}`;

    requestDeduplicator.deduplicate(requestKey, () => StoreService.getStore(storeIdAtStart))
      .then((data) => {
        if (cancelled) {
          return;
        }
        storeDetailsStoreIdRef.current = storeIdAtStart;
        setStoreDetails(data);
        setStoreDetailsRequestId((current) => current + 1);
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        console.error('Failed to load store details for agenda:', err);
        storeDetailsStoreIdRef.current = storeIdAtStart;
        setStoreDetails(null);
        setStoreDetailsRequestId((current) => current + 1);
      });

    return () => {
      cancelled = true;
    };
  }, [currentStore?.id]);

  useEffect(() => {
    if (!currentStore?.id || !canReadAgenda) {
      setTeamMembers([]);
      setSelectedTeamMemberIds([]);
      teamMembersStoreIdRef.current = null;
      commitTeamMemberSelection([]);
      return;
    }

    if (!canReadAllAgendas) {
      const ownTeamMember = { id: ownTeamMemberId, name: ownTeamMemberName, picture: ownTeamMemberPicture };
      setTeamMembers(ownTeamMemberId ? [ownTeamMember] : []);
      setSelectedTeamMemberIds(ownTeamMemberId ? [ownTeamMemberId] : []);
      teamMembersStoreIdRef.current = currentStore.id;
      commitTeamMemberSelection(ownTeamMemberId ? [ownTeamMemberId] : []);
      return;
    }

    setTeamMembers([]);
    setSelectedTeamMemberIds([]);
    teamMembersStoreIdRef.current = null;
    commitTeamMemberSelection([]);

    let cancelled = false;
    const storeIdAtStart = currentStore.id;
    const requestKey = `management-agenda-team-members-${storeIdAtStart}`;

    requestDeduplicator.deduplicate(requestKey, () => TeamMemberService.getTeamMembersByStore(storeIdAtStart))
      .then((data) => {
        if (cancelled || activeStoreIdRef.current !== storeIdAtStart) return;
        const activeTeamMembers = filterAgendaVisibleTeamMembers(Array.isArray(data) ? data : []);
        const allMemberIds = activeTeamMembers.map((teamMember) => teamMember.id);
        setTeamMembers(activeTeamMembers);
        setSelectedTeamMemberIds(allMemberIds);
        teamMembersStoreIdRef.current = storeIdAtStart;
        commitTeamMemberSelection(allMemberIds);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load agenda team members:', err);
          setTeamMembers([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentStore?.id, canReadAgenda, canReadAllAgendas, ownTeamMemberId, ownTeamMemberName, ownTeamMemberPicture, commitTeamMemberSelection]);

  const committedTeamMemberKey = useMemo(
    () => [...committedTeamMemberIds].sort().join(','),
    [committedTeamMemberIds],
  );

  useEffect(() => {
    if (
      !currentStore?.id
      || committedTeamMemberIds.length === 0
      || !canReadAgenda
      || committedForStoreIdRef.current !== currentStore.id
      || teamMembersStoreIdRef.current !== currentStore.id
    ) {
      setAppointments([]);
      setTimeOffs([]);
      setSpecialOpenings([]);
      setAgendaWorkingHours([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const storeIdAtStart = currentStore.id;
    setLoading(true);
    setError(null);

    const requestKey = [
      'management-agenda',
      storeIdAtStart,
      canReadAllAgendas ? 'all' : 'own',
      committedTeamMemberKey,
      dateRange.from,
      dateRange.to,
      agendaRefreshNonce,
    ].join(':');

    const loadAgenda = () => {
      if (
        activeStoreIdRef.current !== storeIdAtStart
        || committedForStoreIdRef.current !== storeIdAtStart
        || teamMembersStoreIdRef.current !== storeIdAtStart
      ) {
        return Promise.resolve({ appointments: [], timeOffs: [], specialOpenings: [] });
      }

      return canReadAllAgendas
        ? getManagementAgendaAppointments(committedTeamMemberIds, dateRange)
        : getOwnManagementAgendaAppointments(dateRange);
    };

    requestDeduplicator.deduplicate(requestKey, loadAgenda)
      .then((data) => {
        if (
          cancelled
          || activeStoreIdRef.current !== storeIdAtStart
          || committedForStoreIdRef.current !== storeIdAtStart
          || teamMembersStoreIdRef.current !== storeIdAtStart
        ) {
          return;
        }

        const nextAppointments = Array.isArray(data?.appointments) ? data.appointments : [];
        setAppointments(nextAppointments);
        setDetailsAppointment((prev) => {
          if (!prev?.id) return prev;
          const refreshed = nextAppointments.find((item) => item.id === prev.id);
          return refreshed || prev;
        });
        setTimeOffs(Array.isArray(data?.timeOffs) ? data.timeOffs : []);
        setSpecialOpenings(Array.isArray(data?.specialOpenings) ? data.specialOpenings : []);
        setAgendaWorkingHours(Array.isArray(data?.workingHours) ? data.workingHours : []);
      })
      .catch((err) => {
        if (
          cancelled
          || activeStoreIdRef.current !== storeIdAtStart
          || committedForStoreIdRef.current !== storeIdAtStart
          || teamMembersStoreIdRef.current !== storeIdAtStart
        ) {
          return;
        }

        console.error('Failed to load management agenda:', err);
        setAppointments([]);
        setTimeOffs([]);
        setSpecialOpenings([]);
        setAgendaWorkingHours([]);
        setError(err?.response?.data?.error || err?.response?.data?.message || t('managementAppointments.loadError'));
      })
      .finally(() => {
        if (
          !cancelled
          && activeStoreIdRef.current === storeIdAtStart
          && committedForStoreIdRef.current === storeIdAtStart
          && teamMembersStoreIdRef.current === storeIdAtStart
        ) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentStore?.id, committedTeamMemberIds, committedTeamMemberKey, dateRange, canReadAgenda, canReadAllAgendas, agendaRefreshNonce, t]);

  const handleAgendaColumnClick = (event, column) => {
    if (!canCreateAppointments) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const offsetY = event.clientY - rect.top - AGENDA_TOP_PADDING;
    if (offsetY < 0) {
      return;
    }

    const rawMinutes = visibleBounds.earliest + (offsetY / pixelsPerMinute);
    const slotInterval = parseSlotIntervalMinutes(storeDetails?.slotInterval);
    const snappedMinutes = snapMinutesToSlot(rawMinutes, slotInterval);

    if (snappedMinutes < visibleBounds.earliest || snappedMinutes >= visibleBounds.latest) {
      return;
    }

    const dayOpeningHours = getColumnOpeningHours(
      storeDetails,
      column,
      specialOpenings,
      selectedTeamMemberIds,
      workingHoursByTeamMemberId,
    );

    const hasBlockingTimeOff = (column.timeOffs || []).some((timeOff) => {
      const renderPeriod = getTimeOffDisplayPeriod(timeOff, visibleBounds, dayOpeningHours.periods);
      const from = parseTimeToMinutes(renderPeriod?.from);
      const to = parseTimeToMinutes(renderPeriod?.to);
      return snappedMinutes >= from && snappedMinutes < to;
    });
    if (hasBlockingTimeOff) {
      return;
    }

    const prefilledTeamMember = column.teamMember
      || (selectedTeamMembers.length === 1 ? selectedTeamMembers[0] : null);

    setBookPrefill(null);
    setBookSlot({
      date: formatDateKey(column.date),
      startTime: formatMinutes(snappedMinutes),
      teamMemberId: prefilledTeamMember?.id,
      teamMemberName: prefilledTeamMember?.name,
    });
  };

  const handleBookFromFab = () => {
    if (!canCreateAppointments || visibleDays.length === 0) {
      return;
    }

    const today = startOfDay(new Date());
    const targetDay = visibleDays.find((day) => isSameCalendarDay(day, today)) || visibleDays[0];
    const slotInterval = parseSlotIntervalMinutes(storeDetails?.slotInterval);
    let startMinutes = visibleBounds.earliest;

    if (isSameCalendarDay(targetDay, today)) {
      startMinutes = snapMinutesToSlot(currentMinutes, slotInterval);
      startMinutes = Math.min(
        Math.max(startMinutes, visibleBounds.earliest),
        Math.max(visibleBounds.earliest, visibleBounds.latest - slotInterval),
      );
    }

    const prefilledTeamMember = selectedTeamMembers.length === 1 ? selectedTeamMembers[0] : null;

    setBookPrefill(null);
    setBookSlot({
      date: formatDateKey(targetDay),
      startTime: formatMinutes(startMinutes),
      teamMemberId: prefilledTeamMember?.id,
      teamMemberName: prefilledTeamMember?.name,
    });
  };

  const closeBookDialog = () => {
    setBookSlot(null);
    setBookPrefill(null);
  };

  const openBookNextAppointment = (appointment) => {
    closeAppointmentDetails();
    setBookPrefill(getBookNextPrefill(appointment));
    setBookSlot({});
  };

  const handleAppointmentBooked = () => {
    closeBookDialog();
    showSuccess(t('managementAppointments.book.success'));
    setAgendaRefreshNonce((current) => current + 1);
  };

  const openCompletion = async (appointment) => {
    const breakdown = getAppointmentCheckoutBreakdown(appointment);
    let productUsage = null;
    if (!hideProducts && currentStore?.id) {
      try {
        productUsage = await requestDeduplicator.deduplicate(
          `appointment-product-usage-${currentStore.id}-${appointment.id}`,
          () => getAppointmentProductUsage(currentStore.id, appointment.id),
        );
      } catch (usageError) {
        console.error('Failed to load appointment product usage:', usageError);
      }
    }

    const hasProductUsage = (productUsage?.lines || []).length > 0;
    if (breakdown.needsCheckout || hasProductUsage) {
      setCheckoutAction({ type: 'complete', appointment, productUsage });
      return;
    }

    setConfirmAction({ type: 'complete', appointment });
  };

  const handleUpdateAppointmentStatus = async (appointment, status, checkout = null, { bookNext = false } = {}) => {
    if (!appointment?.id || !currentStore?.id) return;

    const shouldOpenBookNext = status === AppointmentStatus.Completed
      && bookNext
      && canCreateAppointments;

    // With nothing left to collect, the customer's products (paid online with the booking) are
    // still handed over, so completing sells them rather than dropping them.
    const pendingProductLines = status === AppointmentStatus.Completed && !checkout
      ? getPendingProductSaleLines(appointment)
      : [];
    const statusCheckout = pendingProductLines.length ? { customerProductLines: pendingProductLines } : checkout;
    const wasPaid = getAppointmentCheckoutBreakdown(appointment).paid;

    try {
      setCompletingAppointmentId(appointment.id);
      await updateAppointmentStatus(appointment.id, status, currentStore.id, statusCheckout);

      const nextPaymentStatus = status === AppointmentStatus.Completed ? 2 : appointment.paymentStatus;
      const applyStatusPatch = (item) => {
        if (item.id !== appointment.id) return item;

        const patch = { ...item, status, paymentStatus: nextPaymentStatus };
        if (status === AppointmentStatus.Completed) {
          // What the till took for products on a paid appointment is not the appointment's payment.
          Object.assign(patch, applyPaidCheckoutFields(patch, wasPaid ? null : checkout));
          if (item.pendingMembershipEnrollment) {
            patch.pendingMembershipEnrollment = false;
            patch.pendingMembershipPlanId = null;
            patch.pendingMembershipPlanName = null;
            patch.coveredByMembership = true;
          }
        }
        return patch;
      };

      setAppointments((prev) => prev.map(applyStatusPatch));
      setDetailsAppointment((prev) => (prev?.id === appointment.id ? applyStatusPatch(prev) : prev));
      if (status === AppointmentStatus.Completed && await moloniReadyPromise.current) {
        setInvoicePromptId(appointment.id);
        setAgendaRefreshNonce((current) => current + 1);
        if (shouldOpenBookNext) {
          openBookNextAppointment(appointment);
        }
      }
    } catch (err) {
      console.error('Failed to update appointment status:', err);
      setError(isApiErrorCode(err, 'AppointmentProductsPaidOnline')
        ? t('managementAppointments.prepayment.productsPaidOnlineLocked')
        : (err?.response?.data?.error || err?.response?.data?.message || t('managementAppointments.statusUpdateError')));
    } finally {
      setCompletingAppointmentId(null);
      setConfirmAction(null);
      setCheckoutAction(null);
    }
  };

  const handleRespondOnRequest = async (appointment, action, teamMemberId) => {
    if (!appointment?.id || !currentStore?.id) {
      return;
    }

    const actingTeamMemberId = teamMemberId || resolveOnRequestActingTeamMemberId(appointment, ownTeamMemberId);
    if (!actingTeamMemberId) {
      setError(t('managementAppointments.selectProfessionalToConfirm'));
      return;
    }

    try {
      setRespondingOnRequestId(appointment.id);
      if (action === 'confirm') {
        await confirmOnRequestAppointment(appointment.id, currentStore.id, actingTeamMemberId);
        showSuccess(t('managementAppointments.confirmOnRequestSuccess'));
      } else {
        await declineOnRequestAppointment(appointment.id, currentStore.id, actingTeamMemberId);
        showSuccess(t('managementAppointments.declineOnRequestSuccess'));
      }
      closeAppointmentDetails();
      setAgendaRefreshNonce((current) => current + 1);
    } catch (err) {
      console.error('Failed to respond to on-request appointment:', err);
      setError(err?.response?.data?.error || err?.response?.data?.message || t('managementAppointments.onRequestRespondError'));
    } finally {
      setRespondingOnRequestId(null);
      setConfirmAction(null);
      setOnRequestActingTeamMemberId('');
    }
  };

  const openOnRequestResponse = (appointment, type) => {
    setOnRequestActingTeamMemberId(resolveOnRequestActingTeamMemberId(appointment, ownTeamMemberId));
    setConfirmAction({ type, appointment });
  };

  const handleDeleteAppointment = async (appointment) => {
    if (!appointment?.id || !currentStore?.id) {
      return;
    }

    try {
      setDeletingAppointmentId(appointment.id);
      await deleteAppointment(appointment.id, currentStore.id);
      setAppointments((prev) => prev.filter((item) => item.id !== appointment.id));
      closeAppointmentDetails();
      setAgendaRefreshNonce((current) => current + 1);
    } catch (err) {
      console.error('Failed to delete appointment:', err);
      // Deleting a booking the customer paid for online would keep their money without a refund.
      setError(isApiErrorCode(err, 'PaidAppointmentDeleteNotAllowed')
        ? t('managementAppointments.delete.paidOnline')
        : (err?.response?.data?.error || err?.response?.data?.message || t('managementAppointments.delete.error')));
    } finally {
      setDeletingAppointmentId(null);
      setConfirmAction(null);
    }
  };

  const handleChangeAppointmentToRegular = async (appointment) => {
    if (!appointment?.id || !currentStore?.id) {
      return;
    }

    try {
      setChangingToRegularAppointmentId(appointment.id);
      await changeAppointmentToRegular(appointment.id, currentStore.id);
      const clearMembershipPatch = (item) => {
        if (item.id !== appointment.id) return item;
        return {
          ...item,
          coveredByMembership: false,
          pendingMembershipEnrollment: false,
          pendingMembershipPlanId: null,
          pendingMembershipPlanName: null,
          customerMembershipId: null,
          membershipPlanId: null,
          membershipPlanName: null,
          coveredByServicePackage: false,
          customerServicePackageId: null,
          servicePackageName: null,
          services: Array.isArray(item.services)
            ? item.services.map((service) => ({ ...service, coveredByPackage: false }))
            : item.services,
          amountDue: (() => {
            const extra = Number(item.additionalCostAmount) || 0;
            if (!Array.isArray(item.services)) {
              return item.totalPrice ?? item.amountDue;
            }

            const serviceTotal = item.services.reduce(
              (sum, service) => sum + (service.unitPrice || 0),
              0,
            );
            return serviceTotal + extra;
          })(),
          paymentStatus: 0,
        };
      };
      setAppointments((prev) => prev.map(clearMembershipPatch));
      closeAppointmentDetails();
      setAgendaRefreshNonce((current) => current + 1);
    } catch (err) {
      console.error('Failed to change appointment to regular:', err);
      setError(
        err?.response?.data?.error
        || err?.response?.data?.message
        || t('managementAppointments.changeToRegularError'),
      );
    } finally {
      setChangingToRegularAppointmentId(null);
      setConfirmAction(null);
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmAction?.appointment) {
      return;
    }

    if (confirmAction.type === 'complete') {
      await handleUpdateAppointmentStatus(confirmAction.appointment, AppointmentStatus.Completed);
      return;
    }

    if (confirmAction.type === 'cancel') {
      await handleUpdateAppointmentStatus(confirmAction.appointment, AppointmentStatus.Cancelled);
      return;
    }

    if (confirmAction.type === 'noShow') {
      await handleUpdateAppointmentStatus(confirmAction.appointment, AppointmentStatus.NoShow);
      return;
    }

    if (confirmAction.type === 'changeToRegular') {
      await handleChangeAppointmentToRegular(confirmAction.appointment);
      return;
    }

    if (confirmAction.type === 'delete') {
      await handleDeleteAppointment(confirmAction.appointment);
      return;
    }

    if (confirmAction.type === 'confirmOnRequest') {
      await handleRespondOnRequest(confirmAction.appointment, 'confirm', onRequestActingTeamMemberId);
      return;
    }

    if (confirmAction.type === 'declineOnRequest') {
      await handleRespondOnRequest(confirmAction.appointment, 'decline', onRequestActingTeamMemberId);
    }
  };

  const handleSetServicePrice = async (serviceId, unitPrice) => {
    if (!detailsAppointment?.id || !currentStore?.id) {
      return;
    }

    await setAppointmentServicePrice(detailsAppointment.id, serviceId, unitPrice, currentStore.id);
    const patch = (item) => (
      item.id === detailsAppointment.id
        ? applyAppointmentServicePrice(item, serviceId, unitPrice)
        : item
    );
    setAppointments((prev) => prev.map(patch));
    setDetailsAppointment((prev) => (prev?.id === detailsAppointment.id ? patch(prev) : prev));
    setAgendaRefreshNonce((current) => current + 1);
  };

  const handleAppointmentUpdated = () => {
    setEditAppointment(null);
    closeAppointmentDetails();
    setAgendaRefreshNonce((current) => current + 1);
  };

  const handleMarkAppointmentPaid = async (appointment, checkout = null) => {
    if (!appointment?.id || !currentStore?.id) {
      return;
    }

    try {
      setMarkingPaidAppointmentId(appointment.id);
      await markAppointmentPaid(appointment.id, currentStore.id, checkout);
      const applyPaidPatch = (item) => (
        item.id === appointment.id ? applyPaidCheckoutFields(item, checkout) : item
      );
      setAppointments((prev) => prev.map(applyPaidPatch));
      setDetailsAppointment((prev) => (prev?.id === appointment.id ? applyPaidPatch(prev) : prev));
      if (await moloniReadyPromise.current) setInvoicePromptId(appointment.id);
    } catch (err) {
      console.error('Failed to mark appointment as paid:', err);
      setError(err?.response?.data?.error || err?.response?.data?.message || t('managementAppointments.markPaidError'));
    } finally {
      setMarkingPaidAppointmentId(null);
      setConfirmAction(null);
      setCheckoutAction(null);
    }
  };

  const appointmentsByColumn = useMemo(() => {
    const grouped = new Map();

    appointments.forEach((appointment) => {
      const services = Array.isArray(appointment.services) ? appointment.services : [];

      if (isMultiTeamView) {
        selectedTeamMembers.forEach((teamMember) => {
          const displayServices = services.filter((service) => service.teamMemberId === teamMember.id);
          if (displayServices.length === 0) return;

          if (!grouped.has(teamMember.id)) grouped.set(teamMember.id, []);
          grouped.get(teamMember.id).push({ ...appointment, displayServices, displayPeriod: getDisplayPeriod(displayServices, appointment.period) });
        });
        return;
      }

      const key = appointment.date;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push({ ...appointment, displayServices: services, displayPeriod: getDisplayPeriod(services, appointment.period) });
    });

    grouped.forEach((columnAppointments) => {
      columnAppointments.sort((left, right) => parseTimeToMinutes(left?.displayPeriod?.from || left?.period?.from) - parseTimeToMinutes(right?.displayPeriod?.from || right?.period?.from));
    });

    return grouped;
  }, [appointments, isMultiTeamView, selectedTeamMembers]);

  const timeOffsByColumn = useMemo(() => {
    const grouped = new Map();
    const agendaDateKey = visibleDays[0] ? formatDateKey(visibleDays[0]) : null;

    timeOffs.forEach((timeOff) => {
      if (isMultiTeamView) {
        if (timeOff.date !== agendaDateKey) return;
        const key = timeOff.teamMemberId;
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key).push(timeOff);
        return;
      }

      const key = timeOff.date;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(timeOff);
    });

    grouped.forEach((columnTimeOffs) => {
      columnTimeOffs.sort((left, right) => {
        const leftFrom = left?.period?.from ? parseTimeToMinutes(left.period.from) : 0;
        const rightFrom = right?.period?.from ? parseTimeToMinutes(right.period.from) : 0;
        return leftFrom - rightFrom;
      });
    });

    return grouped;
  }, [timeOffs, isMultiTeamView, visibleDays]);

  const agendaColumns = useMemo(() => {
    if (isMultiTeamView) {
      const agendaDate = visibleDays[0];
      return selectedTeamMembers.map((teamMember) => ({
        key: teamMember.id,
        date: agendaDate,
        teamMember,
        appointments: appointmentsByColumn.get(teamMember.id) || [],
        timeOffs: timeOffsByColumn.get(teamMember.id) || [],
      }));
    }

    return visibleDays.map((day) => {
      const dayKey = formatDateKey(day);
      return {
        key: dayKey,
        date: day,
        appointments: appointmentsByColumn.get(dayKey) || [],
        timeOffs: timeOffsByColumn.get(dayKey) || [],
      };
    });
  }, [appointmentsByColumn, timeOffsByColumn, isMultiTeamView, selectedTeamMembers, visibleDays]);

  const handleProfessionalSelectClose = () => {
    if (
      !currentStore?.id
      || teamMembers.length === 0
      || teamMembersStoreIdRef.current !== currentStore.id
    ) {
      return;
    }

    const validSelection = selectedTeamMemberIds.filter((teamMemberId) => (
      teamMembers.some((teamMember) => teamMember.id === teamMemberId)
    ));

    if (validSelection.length === 0) {
      return;
    }

    commitTeamMemberSelection(validSelection);
  };

  const handleTeamMemberSelection = (event) => {
    const nextValue = typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;

    if (nextValue.includes(ALL_TEAM_MEMBERS_VALUE)) {
      // Toggle: if all selected → deselect all; otherwise → select all
      setSelectedTeamMemberIds(allTeamMembersSelected ? [] : teamMembers.map((teamMember) => teamMember.id));
      return;
    }

    const deduplicatedSelection = [...new Set(nextValue.filter(Boolean))];
    setSelectedTeamMemberIds(deduplicatedSelection);
  };

  const handleNavigateRange = (direction) => {
    setAnchorDate((prev) => addDays(prev, direction * effectiveViewDays));
  };

  const handleToday = () => {
    setAnchorDate(startOfDay(new Date()));
  };

  const agendaBodyHeight = totalMinutes * pixelsPerMinute + AGENDA_TOP_PADDING + AGENDA_BOTTOM_PADDING;

  const scrollableAgendaColumnTemplate = isMultiTeamView
    ? `repeat(${agendaColumns.length}, minmax(${TEAM_MEMBER_COLUMN_MIN_WIDTH}px, 1fr))`
    : `repeat(${agendaColumns.length}, minmax(${DAY_COLUMN_MIN_WIDTH}px, 1fr))`;

  if (!currentStore) {
    return (
      <ManagementPageLayout
        id="management-appointments-page"
        animateHeader={false}
        sx={agendaPageLayoutSx}
        maxWidth="100%"
        contentSx={agendaPageContentSx}
      >
        <ManagementPageCard sx={{ ...agendaPageCardSx, p: 3 }}>
          <Alert severity="info">
            {t('managementAppointments.selectStore')}
          </Alert>
        </ManagementPageCard>
      </ManagementPageLayout>
    );
  }

  if (permissionsLoading) {
    return (
      <ManagementPageSkeleton
        hideHeader
        variant="agenda"
        maxWidth="100%"
        contentSx={agendaPageContentSx}
        sx={agendaPageLayoutSx}
        cardSx={agendaPageCardSx}
      />
    );
  }

  return (
    <ManagementPageLayout
      id="management-appointments-page"
      animateHeader={false}
      sx={agendaPageLayoutSx}
      maxWidth="100%"
      contentSx={agendaPageContentSx}
    >
      <ManagementPageCard sx={agendaPageCardSx}>
              {selectedTeamMemberIds.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6, color: mutedText }}>
                  <Typography variant="body1">{t('managementAppointments.selectProfessionalToView')}</Typography>
                </Box>
              ) : (
              <Paper elevation={0} sx={{ borderRadius: 0, overflow: 'hidden', bgcolor: BRAND.surface, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <AgendaCalendarToolbar
          t={t}
          isPhone={isPhone}
          canReadAllAgendas={canReadAllAgendas}
          selectedTeamMemberIds={selectedTeamMemberIds}
          allTeamMembersSelected={allTeamMembersSelected}
          selectedTeamMembers={selectedTeamMembers}
          teamMembers={teamMembers}
          onTeamMemberChange={handleTeamMemberSelection}
          onProfessionalSelectClose={handleProfessionalSelectClose}
          isMultiTeamView={isMultiTeamView}
          effectiveViewDays={effectiveViewDays}
          onViewDaysChange={(_, value) => value && setViewDays(value)}
          onToday={handleToday}
          onNavigateRange={handleNavigateRange}
          rangeLabel={getRangeLabel(visibleDays, locale, isPhone)}
          ownTeamMemberName={ownTeamMemberName}
          selectedTeamMember={selectedTeamMember}
          getTeamMemberPicture={getTeamMemberPicture}
          getInitials={getInitials}
          hideProfessionalFilter={hideProfessionalPicker}
        />

        {loading ? (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <ManagementContentSkeleton variant="agenda" includeToolbar={false} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `${TIME_COLUMN_WIDTH}px minmax(0, 1fr)`,
                flexShrink: 0,
                zIndex: AGENDA_STICKY_HEADER_Z_INDEX,
                bgcolor: headerBackground,
              }}
            >
              <Box
                sx={{
                  bgcolor: headerBackground,
                  borderRight: `1px solid ${borderColor}`,
                  borderBottom: `1px solid ${borderColor}`,
                }}
              />
              <Box sx={{ display: 'flex', minWidth: 0, borderBottom: `1px solid ${borderColor}` }}>
                <Box
                  ref={headerScrollRef}
                  onScroll={handleHeaderScroll}
                  sx={{
                    flex: 1,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    minWidth: 0,
                    bgcolor: headerBackground,
                    ...hiddenScrollbarSx,
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: scrollableAgendaColumnTemplate,
                      minWidth: 'max-content',
                      width: '100%',
                      bgcolor: headerBackground,
                    }}
                  >
                  {agendaColumns.map((column) => (
                    <Box key={column.key} sx={{ p: { xs: 0.5, md: 1 }, textAlign: 'center', bgcolor: headerBackground, borderLeft: `1px solid ${borderColor}`, overflow: 'hidden' }}>
                      {isMultiTeamView ? (
                        <Stack spacing={0.75} alignItems="center">
                          <Avatar src={getTeamMemberPicture(column.teamMember) || undefined} sx={{ width: { xs: 28, md: 36 }, height: { xs: 28, md: 36 } }}>
                            {getInitials(column.teamMember?.name)}
                          </Avatar>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BRAND.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>{column.teamMember?.name}</Typography>
                        </Stack>
                      ) : isPhone && viewDays === 7 ? (
                        <Stack spacing={0.5} alignItems="center" sx={{ fontSize: '0.75rem' }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: BRAND.text, fontSize: '0.75rem' }}>{getWeekdayInitial(column.date, locale)}</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: mutedText, fontSize: '0.7rem' }}>{getDayOnly(column.date)}</Typography>
                        </Stack>
                      ) : (
                        <>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BRAND.text }}>{getWeekdayLabel(column.date, locale)}</Typography>
                          <Typography variant="body2" sx={{ color: mutedText }}>{isPhone ? getDayOnly(column.date) : getDateLabel(column.date, locale)}</Typography>
                        </>
                      )}
                    </Box>
                  ))}
                  </Box>
                </Box>
                <Box
                  aria-hidden
                  sx={{
                    flexShrink: 0,
                    width: verticalScrollbarWidth,
                    bgcolor: headerBackground,
                  }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `${TIME_COLUMN_WIDTH}px minmax(0, 1fr)`,
                flex: 1,
                minHeight: 0,
              }}
            >
              <Box
                ref={timeScrollRef}
                onScroll={handleTimeScroll}
                sx={{
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  height: '100%',
                  bgcolor: hourColumnBackground,
                  borderRight: `1px solid ${borderColor}`,
                  overscrollBehavior: 'contain',
                  ...hiddenScrollbarSx,
                }}
              >
                <Box sx={{ position: 'relative', height: agendaBodyHeight }}>
                  {hourMarks.map((minute) => (
                    <Box key={minute} sx={{ position: 'absolute', top: AGENDA_TOP_PADDING + (minute - visibleBounds.earliest) * pixelsPerMinute - 9, left: 0, right: 0, px: 0.5, textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ color: mutedText, fontWeight: 600, fontSize: '0.7rem', display: 'block', textAlign: 'center' }}>{formatMinutes(minute)}</Typography>
                    </Box>
                  ))}
                  {showCurrentTimeLine && agendaColumns.some((column) => isSameCalendarDay(column.date, currentTime)) && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: AGENDA_TOP_PADDING + (currentMinutes - visibleBounds.earliest) * pixelsPerMinute + 1,
                        left: CURRENT_TIME_LABEL_SIDE_MARGIN,
                        width: TIME_COLUMN_WIDTH - CURRENT_TIME_LABEL_SIDE_MARGIN * 2,
                        transform: 'translateY(-50%)',
                        zIndex: CURRENT_TIME_LINE_Z_INDEX,
                        pointerEvents: 'none',
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          px: 0.5,
                          py: 0.1,
                          borderRadius: '999px',
                          bgcolor: '#ef4444',
                          color: '#ffffff',
                          fontSize: '0.62rem',
                          fontWeight: 700,
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap',
                          textAlign: 'center',
                        }}
                      >
                        {formatMinutes(currentMinutes)}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
              <Box
                ref={mainScrollRef}
                onScroll={handleMainScroll}
                sx={{
                  overflow: 'auto',
                  minWidth: 0,
                  height: '100%',
                  overscrollBehavior: 'contain',
                  scrollbarGutter: 'stable',
                }}
              >
                <Box ref={contentRef} sx={{ minWidth: 'max-content', width: '100%', position: 'relative', height: agendaBodyHeight }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: scrollableAgendaColumnTemplate, position: 'relative', height: agendaBodyHeight }}>
                {agendaColumns.map((column) => {
                  const dayOpeningHours = getColumnOpeningHours(
                    storeDetails,
                    column,
                    specialOpenings,
                    selectedTeamMemberIds,
                    workingHoursByTeamMemberId,
                  );
                  const openingPeriods = dayOpeningHours.periods;
                  const closedSegments = getClosedSegments(openingPeriods, visibleBounds, dayOpeningHours.openingHoursEntry);
                  return (
                    <Box
                      key={column.key}
                      onClick={canCreateAppointments ? (event) => handleAgendaColumnClick(event, column) : undefined}
                      sx={{
                        position: 'relative',
                        height: agendaBodyHeight,
                        borderLeft: `1px solid ${borderColor}`,
                        bgcolor: BRAND.surface,
                        cursor: canCreateAppointments ? 'pointer' : 'default',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: AGENDA_TOP_PADDING,
                          bgcolor: closedSegmentBackground,
                        }}
                      />
                      {hourMarks.map((minute) => (
                        <Box
                          key={`${column.key}-${minute}`}
                          sx={{
                            position: 'absolute',
                            top: AGENDA_TOP_PADDING + (minute - visibleBounds.earliest) * pixelsPerMinute,
                            left: 0,
                            right: 0,
                            height: '1px',
                            bgcolor: borderColor,
                            zIndex: 1,
                          }}
                        />
                      ))}
                      {closedSegments.map((segment) => (
                        <Box
                          key={`${column.key}-${segment.from}-${segment.to}`}
                          sx={{
                            position: 'absolute',
                            top: AGENDA_TOP_PADDING + (segment.from - visibleBounds.earliest) * pixelsPerMinute,
                            left: 0,
                            right: 0,
                            height: (segment.to - segment.from) * pixelsPerMinute,
                            bgcolor: closedSegmentBackground,
                            zIndex: 0,
                          }}
                        />
                      ))}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: AGENDA_BOTTOM_PADDING,
                          bgcolor: closedSegmentBackground,
                        }}
                      />
                      {(column.timeOffs || []).map((timeOff) => {
                        const renderPeriod = getTimeOffDisplayPeriod(timeOff, visibleBounds, openingPeriods);
                        const from = parseTimeToMinutes(renderPeriod?.from);
                        const to = parseTimeToMinutes(renderPeriod?.to);
                        const top = (from - visibleBounds.earliest) * pixelsPerMinute + 1;
                        const height = Math.max(MIN_APPOINTMENT_HEIGHT, (to - from) * pixelsPerMinute - 2);
                        const fullDay = isFullDayTimeOff(timeOff);
                        return (
                          <Box
                            key={`${column.key}-timeoff-${timeOff.id}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              setDetailsTimeOff({ ...timeOff, displayPeriod: renderPeriod, fullDay });
                            }}
                            sx={{
                              position: 'absolute',
                              top: AGENDA_TOP_PADDING + top,
                              left: APPOINTMENT_HORIZONTAL_INSET,
                              right: APPOINTMENT_HORIZONTAL_INSET,
                              height,
                              borderRadius: 2,
                              borderLeft: `4px solid ${timeOffColors.border}`,
                              bgcolor: timeOffColors.background,
                              color: timeOffColors.text,
                              px: 1.25,
                              py: 0.75,
                              overflow: 'hidden',
                              cursor: 'pointer',
                              boxShadow: '0 1px 2px rgba(15,23,42,0.08)',
                              zIndex: 1,
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2, mb: 0.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {t('managementAppointments.timeOff')}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, lineHeight: 1.25 }}>
                              {t(`teamMembers.timeOff.types.${timeOff.type}`)}
                              {!isMultiTeamView ? ` · ${timeOff.teamMemberName}` : ''}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, lineHeight: 1.1, mt: 0.35, opacity: 0.9 }}>
                              {fullDay
                                ? t('teamMembers.timeOff.fullDay')
                                : `${renderPeriod?.from?.slice(0, 5)} - ${renderPeriod?.to?.slice(0, 5)}`}
                            </Typography>
                          </Box>
                        );
                      })}
                      {layoutOverlappingAppointments(column.appointments).map(({ appointment, stackIndex, stackCount }) => (
                        <AgendaAppointmentCard
                          key={`${column.key}-${appointment.id}`}
                          appointment={appointment}
                          stackIndex={stackIndex}
                          stackCount={stackCount}
                          visibleBounds={visibleBounds}
                          pixelsPerMinute={pixelsPerMinute}
                          languageCode={languageCode}
                          t={t}
                          onSelect={openAppointmentDetails}
                          invoiceLabel={moloniReady && invoiceFlags[String(appointment.id).replace(/-/g, '')] ? t('moloni.issued') : ''}
                        />
                      ))}
                    </Box>
                  );
                })}
                {showCurrentTimeLine && agendaColumns.some((column) => isSameCalendarDay(column.date, currentTime)) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: CURRENT_TIME_LINE_Z_INDEX,
                      pointerEvents: 'none',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: AGENDA_TOP_PADDING + (currentMinutes - visibleBounds.earliest) * pixelsPerMinute,
                        left: 0,
                        right: 0,
                        height: '2px',
                        bgcolor: '#ef4444',
                        boxShadow: '0 0 0 1px rgba(239, 68, 68, 0.08)',
                      }}
                    />
                  </Box>
                )}
                </Box>
              </Box>
              </Box>
            </Box>
          </Box>
        )}
        {canCreateAppointments && (
          <Fab
            color="primary"
            aria-label={t('managementAppointments.bookFab')}
            onClick={handleBookFromFab}
            sx={{
              position: 'absolute',
              right: 16,
              bottom: { xs: 'calc(16px + env(safe-area-inset-bottom, 0px))', md: 16 },
              zIndex: 9,
              background: BRAND.accent,
              color: BRAND.onAccent,
              boxShadow: `0 6px 16px ${brandAlpha('primary', 0.18)}`,
              '&:hover': {
                background: BRAND.accentDark,
              },
            }}
          >
            <AddIcon />
          </Fab>
        )}
            </Paper>
              )}
          </ManagementPageCard>

      <AppointmentDetailsDrawer
        open={Boolean(detailsAppointment)}
        onClose={closeAppointmentDetails}
        appointment={detailsAppointment}
        languageCode={languageCode}
        storeLanguage={storeDetails?.supportedLanguages?.[0] || storeDetails?.storeLanguage}
        teamMembers={teamMembers}
        currencyCode={storeDetails?.currency || DEFAULT_CURRENCY}
        hideProfessional={hideProfessionalPicker}
        hideProducts={hideProducts}
        resolveServiceName={(name) => resolveServiceName(name, languageCode, t)}
        canMarkPaid={canMarkPaid && detailsAppointment && canMarkAppointmentPaid(detailsAppointment)}
        canIssueInvoice={moloniReady && (canMarkPaid || hasPermission(Permissions.ExpensesWrite))}
        canUpdateStatus={detailsAppointment && canUpdateAppointmentStatus(detailsAppointment, canReadAllAgendas, ownTeamMemberId)}
        canEdit={detailsAppointment && canEditAppointment(
          detailsAppointment,
          canUpdateAllAppointments,
          canUpdateOwnAppointments,
          ownTeamMemberId,
        )}
        canDelete={detailsAppointment && canDeleteAppointment(detailsAppointment, canDeleteAppointments)}
        canSetServicePrice={hasPermission(Permissions.AppointmentsPriceUpdate)}
        onSetServicePrice={handleSetServicePrice}
        canChangeToRegular={detailsAppointment && canEditAppointment(
          detailsAppointment,
          canUpdateAllAppointments,
          canUpdateOwnAppointments,
          ownTeamMemberId,
        ) && (detailsAppointment.coveredByMembership
          || detailsAppointment.pendingMembershipEnrollment
          || detailsAppointment.coveredByServicePackage)}
        onMarkPaid={() => {
          if (getAppointmentCheckoutBreakdown(detailsAppointment).needsCheckout
            && !detailsAppointment.pendingMembershipEnrollment) {
            setCheckoutAction({ type: 'markPaid', appointment: detailsAppointment });
            return;
          }

          setConfirmAction({ type: 'markPaid', appointment: detailsAppointment });
        }}
        onMarkCompleted={() => openCompletion(detailsAppointment)}
        onMarkCancelled={() => setConfirmAction({ type: 'cancel', appointment: detailsAppointment })}
        onMarkNoShow={() => setConfirmAction({ type: 'noShow', appointment: detailsAppointment })}
        onConfirmOnRequest={() => openOnRequestResponse(detailsAppointment, 'confirmOnRequest')}
        onDeclineOnRequest={() => openOnRequestResponse(detailsAppointment, 'declineOnRequest')}
        onEdit={() => setEditAppointment(detailsAppointment)}
        onDelete={() => setConfirmAction({ type: 'delete', appointment: detailsAppointment })}
        onChangeToRegular={() => setConfirmAction({ type: 'changeToRegular', appointment: detailsAppointment })}
        markingPaid={Boolean(detailsAppointment && markingPaidAppointmentId === detailsAppointment.id)}
        updatingStatus={Boolean(
          detailsAppointment
          && (completingAppointmentId === detailsAppointment.id || respondingOnRequestId === detailsAppointment.id),
        )}
        changingToRegular={Boolean(detailsAppointment && changingToRegularAppointmentId === detailsAppointment.id)}
        storeId={currentStore?.id}
        canViewCustomer={canReadCustomers}
        onCustomerClick={() => {
          if (!currentStore?.id || !detailsAppointment?.customerId) {
            return;
          }

          const from = buildManagementAppointmentsPath({
            appointmentId: detailsAppointment.id,
            date: detailsAppointment.date,
          });
          const params = new URLSearchParams({ from });
          navigate(`/management/stores/${currentStore.id}/customers/${detailsAppointment.customerId}?${params.toString()}`);
        }}
      />

      <IssueInvoiceDialog
        open={Boolean(invoicePromptId)}
        storeId={currentStore?.id}
        sourceKey={invoicePromptId ? String(invoicePromptId).replace(/-/g, '') : ''}
        onClose={() => setInvoicePromptId(null)}
        onIssued={() => {
          const key = String(invoicePromptId).replace(/-/g, '');
          setInvoiceFlags((current) => ({ ...current, [key]: true }));
          setInvoicePromptId(null);
        }}
      />

      <ConfirmDialog
        open={Boolean(confirmAction)}
        onClose={() => {
          setConfirmAction(null);
          setOnRequestActingTeamMemberId('');
        }}
        onConfirm={async () => {
          if (confirmAction?.type === 'markPaid') {
            await handleMarkAppointmentPaid(confirmAction.appointment);
            setConfirmAction(null);
            return;
          }

          await handleConfirmAction();
        }}
        onSecondaryConfirm={
          confirmAction?.type === 'complete' && canCreateAppointments
            ? async () => {
              await handleUpdateAppointmentStatus(
                confirmAction.appointment,
                AppointmentStatus.Completed,
                null,
                { bookNext: true },
              );
            }
            : undefined
        }
        title={
          confirmAction?.type === 'complete' && confirmAction?.appointment?.pendingMembershipEnrollment
            ? t('managementAppointments.confirm.completeEnrollTitle')
            : confirmAction?.type === 'complete'
              ? t('managementAppointments.confirm.completeTitle')
              : confirmAction?.type === 'cancel'
                ? t('managementAppointments.confirm.cancelTitle')
                : confirmAction?.type === 'noShow'
                  ? t('managementAppointments.confirm.noShowTitle')
                  : confirmAction?.type === 'delete'
                  ? t('managementAppointments.confirm.deleteTitle')
                  : confirmAction?.type === 'markPaid'
                    ? t('managementAppointments.confirm.markPaidTitle')
                    : confirmAction?.type === 'changeToRegular'
                      ? t('managementAppointments.confirm.changeToRegularTitle')
                      : confirmAction?.type === 'confirmOnRequest'
                        ? t('managementAppointments.confirm.confirmOnRequestTitle')
                        : confirmAction?.type === 'declineOnRequest'
                          ? t('managementAppointments.confirm.declineOnRequestTitle')
                      : t('common.confirm')
        }
        message={(() => {
          if (confirmAction?.type === 'complete' && confirmAction?.appointment?.pendingMembershipEnrollment) {
            const planLabel = getLocalizedValue(
              confirmAction.appointment.membershipPlanName,
              languageCode,
              storeDetails?.supportedLanguages?.[0] || storeDetails?.storeLanguage,
              '',
            );
            const coveredByMembership = Boolean(confirmAction.appointment.coveredByMembership);
            const amountDue = Number(
              confirmAction.appointment.amountDue
              ?? (coveredByMembership ? 0 : confirmAction.appointment.totalPrice)
              ?? 0,
            );
            return t('managementAppointments.confirm.completeEnrollMessage', {
              plan: planLabel || t('managementAppointments.pendingMembershipEnrollment'),
              amount: formatCurrency(amountDue, storeDetails?.currency || DEFAULT_CURRENCY),
            });
          }

          if (confirmAction?.type === 'complete') {
            return t('managementAppointments.confirm.completeMessage');
          }

          if (confirmAction?.type === 'cancel' || confirmAction?.type === 'noShow') {
            return t(resolveStaffStatusMessageKey(confirmAction.type, confirmAction.appointment));
          }

          if (confirmAction?.type === 'delete') {
            return t('managementAppointments.confirm.deleteMessage');
          }

          if (confirmAction?.type === 'markPaid') {
            return t('managementAppointments.confirm.markPaidMessage');
          }

          if (confirmAction?.type === 'changeToRegular') {
            return t(confirmAction.appointment?.coveredByServicePackage
              ? 'servicePackagesManagement.appointment.changeToRegularMessage'
              : 'managementAppointments.confirm.changeToRegularMessage');
          }

          if (confirmAction?.type === 'confirmOnRequest') {
            return t('managementAppointments.confirm.confirmOnRequestMessage');
          }

          if (confirmAction?.type === 'declineOnRequest') {
            return t('managementAppointments.confirm.declineOnRequestMessage');
          }

          return '';
        })()}
        confirmText={
          confirmAction?.type === 'complete'
            ? t('common.confirm')
            : confirmAction?.type === 'cancel'
              ? t('managementAppointments.markCancelled')
              : confirmAction?.type === 'noShow'
                ? t('managementAppointments.markNoShow')
                : confirmAction?.type === 'delete'
                ? t('managementAppointments.delete.action')
                : confirmAction?.type === 'markPaid'
                  ? t('managementAppointments.markPaid')
                  : confirmAction?.type === 'changeToRegular'
                    ? t('managementAppointments.changeToRegular')
                    : confirmAction?.type === 'confirmOnRequest'
                      ? t('managementAppointments.confirmOnRequest')
                      : confirmAction?.type === 'declineOnRequest'
                        ? t('managementAppointments.declineOnRequest')
                    : t('common.confirm')
        }
        secondaryConfirmText={
          confirmAction?.type === 'complete' && canCreateAppointments
            ? t('managementAppointments.confirm.completeAndBookNext')
            : undefined
        }
        confirmColor={
          confirmAction?.type === 'complete'
          || confirmAction?.type === 'markPaid'
          || confirmAction?.type === 'changeToRegular'
          || confirmAction?.type === 'confirmOnRequest'
            ? 'primary'
            : 'error'
        }
        confirmDisabled={
          (confirmAction?.type === 'confirmOnRequest' || confirmAction?.type === 'declineOnRequest')
          && !onRequestActingTeamMemberId
        }
        loading={Boolean(
          completingAppointmentId
          || markingPaidAppointmentId
          || deletingAppointmentId
          || changingToRegularAppointmentId
          || respondingOnRequestId,
        )}
      >
        {(confirmAction?.type === 'confirmOnRequest' || confirmAction?.type === 'declineOnRequest')
          && getPendingOnRequestCandidateIds(confirmAction.appointment).length > 1
          && (
            <Box sx={{ mt: 2 }}>
              <SalovareSelectField
                label={t('managementAppointments.selectProfessionalToConfirm')}
                value={onRequestActingTeamMemberId}
                onChange={(event) => setOnRequestActingTeamMemberId(event.target.value)}
                displayEmpty
              >
                <MenuItem value="">
                  <em>{t('managementAppointments.selectProfessionalToConfirm')}</em>
                </MenuItem>
                {getPendingOnRequestCandidateIds(confirmAction.appointment).map((teamMemberId) => {
                  const member = teamMembers.find((item) => String(item.id) === String(teamMemberId));
                  const serviceMatch = (confirmAction.appointment?.services || [])
                    .find((service) => String(service.teamMemberId) === String(teamMemberId));
                  return (
                    <MenuItem key={teamMemberId} value={teamMemberId}>
                      {member?.name || serviceMatch?.teamMemberName || teamMemberId}
                    </MenuItem>
                  );
                })}
              </SalovareSelectField>
            </Box>
          )}
      </ConfirmDialog>

      <AppointmentCheckoutDialog
        open={Boolean(checkoutAction)}
        onClose={() => setCheckoutAction(null)}
        confirming={Boolean(
          checkoutAction?.appointment
          && (
            completingAppointmentId === checkoutAction.appointment.id
            || markingPaidAppointmentId === checkoutAction.appointment.id
          ),
        )}
        title={
          checkoutAction?.type === 'complete' && checkoutAction?.appointment?.pendingMembershipEnrollment
            ? t('managementAppointments.confirm.completeEnrollTitle')
            : checkoutAction?.type === 'complete'
              ? t('managementAppointments.confirm.completeTitle')
              : t('managementAppointments.confirm.markPaidTitle')
        }
        confirmText={
          checkoutAction?.type === 'complete'
            ? t('common.confirm')
            : t('managementAppointments.markPaid')
        }
        secondaryConfirmText={
          checkoutAction?.type === 'complete' && canCreateAppointments
            ? t('managementAppointments.confirm.completeAndBookNext')
            : undefined
        }
        currencyCode={storeDetails?.currency || DEFAULT_CURRENCY}
        membershipFee={
          checkoutAction?.type === 'complete'
            ? getAppointmentCheckoutBreakdown(checkoutAction?.appointment).membershipFee
            : 0
        }
        amountDue={getAppointmentCheckoutBreakdown(checkoutAction?.appointment).collectableDue}
        appointment={checkoutAction?.appointment}
        productUsage={checkoutAction?.productUsage}
        canSeeCosts={hasPermission(Permissions.StockCostsRead)}
        allowDiscount={canDiscountAppointments}
        onConfirm={async (checkout) => {
          if (checkoutAction?.type === 'markPaid') {
            await handleMarkAppointmentPaid(checkoutAction.appointment, checkout);
            return;
          }

          if (checkoutAction?.type === 'complete') {
            await handleUpdateAppointmentStatus(checkoutAction.appointment, AppointmentStatus.Completed, checkout);
          }
        }}
        onSecondaryConfirm={
          checkoutAction?.type === 'complete' && canCreateAppointments
            ? async (checkout) => {
              await handleUpdateAppointmentStatus(
                checkoutAction.appointment,
                AppointmentStatus.Completed,
                checkout,
                { bookNext: true },
              );
            }
            : undefined
        }
      />

      <ScrollableDialog
        open={Boolean(detailsTimeOff)}
        onClose={() => setDetailsTimeOff(null)}
        maxWidth="sm"
        title={t('managementAppointments.timeOffDetailsTitle')}
        dividers
        actionsSx={{ justifyContent: 'flex-end' }}
        actions={<Button onClick={() => setDetailsTimeOff(null)}>{t('common.close')}</Button>}
      >
        {detailsTimeOff ? (
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">{t('managementAppointments.professional')}</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{detailsTimeOff.teamMemberName}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">{t('managementAppointments.timeOffType')}</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{t(`teamMembers.timeOff.types.${detailsTimeOff.type}`)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">{t('managementAppointments.timeRange')}</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {detailsTimeOff.fullDay
                  ? t('teamMembers.timeOff.fullDay')
                  : `${detailsTimeOff.displayPeriod?.from?.slice(0, 5)} - ${detailsTimeOff.displayPeriod?.to?.slice(0, 5)}`}
              </Typography>
            </Box>
            {detailsTimeOff.details ? (
              <Box>
                <Typography variant="caption" color="text.secondary">{t('managementAppointments.additionalInfo')}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{detailsTimeOff.details}</Typography>
              </Box>
            ) : null}
          </Stack>
        ) : null}
      </ScrollableDialog>

      <BookAppointmentDialog
        open={Boolean(bookSlot)}
        onClose={closeBookDialog}
        storeId={currentStore?.id}
        slotContext={bookSlot}
        teamMembers={teamMembers}
        workingHoursByTeamMemberId={workingHoursByTeamMemberId}
        storeDetails={storeDetails}
        storeLanguage={storeDetails?.supportedLanguages?.[0] || storeDetails?.storeLanguage}
        existingAppointments={appointments}
        initialCustomer={bookPrefill?.customer}
        initialServiceIds={bookPrefill?.serviceIds || EMPTY_BOOK_SERVICE_IDS}
        initialCustomerMembershipId={bookPrefill?.customerMembershipId}
        initialMembershipPlanId={bookPrefill?.membershipPlanId}
        hideProfessionals={hideProfessionalPicker}
        hideProducts={hideProducts}
        onBooked={handleAppointmentBooked}
      />

      <EditAppointmentDialog
        open={Boolean(editAppointment)}
        onClose={() => setEditAppointment(null)}
        appointment={editAppointment}
        storeId={currentStore?.id}
        teamMembers={teamMembers}
        workingHoursByTeamMemberId={workingHoursByTeamMemberId}
        storeDetails={storeDetails}
        storeLanguage={storeDetails?.supportedLanguages?.[0] || storeDetails?.storeLanguage}
        existingAppointments={appointments}
        onUpdated={handleAppointmentUpdated}
      />
    </ManagementPageLayout>
  );
}
